/**
 * VoiceCommandButton — Grava áudio e transcreve via Whisper (Edge Function).
 * Padrão: limite 5 min (300s), webm, feedback claro em erros.
 * Substitui VoiceRecordButton (Web Speech API) por maior precisão.
 */

import { useState, useRef, useCallback, useEffect } from "react";
import { Mic, MicOff, Loader2 } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { toast } from "sonner";
import { supabase } from "@/shared/integrations/supabase/client";

const MAX_RECORD_SECONDS = 300; // 5 minutos conforme padrão de padronização

interface VoiceCommandButtonProps {
  onTranscript: (text: string) => void;
  disabled?: boolean;
  className?: string;
}

const VoiceCommandButton = ({ onTranscript, disabled, className }: VoiceCommandButtonProps) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(MAX_RECORD_SECONDS);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const stopRecordingRef = useRef<() => void>(() => {});

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startRecording = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Seu navegador não suporta gravação de áudio.");
      return;
    }
    if (!window.isSecureContext) {
      toast.error("Gravação por voz exige HTTPS ou localhost.");
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/webm";
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
      };

      recorder.start(1000);
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
      setSecondsLeft(MAX_RECORD_SECONDS);

      timerRef.current = setInterval(() => {
        setSecondsLeft((s) => {
          if (s <= 1) {
            stopTimer();
            if (mediaRecorderRef.current?.state === "recording") {
              stopRecordingRef.current();
            }
            return 0;
          }
          return s - 1;
        });
      }, 1000);
    } catch (err) {
      toast.error("Não foi possível acessar o microfone. Verifique as permissões.");
    }
  }, [stopTimer]);

  const stopRecording = useCallback(() => {
    stopTimer();
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state !== "recording") {
      setIsRecording(false);
      return;
    }

    setIsProcessing(true);
    recorder.onstop = async () => {
      setIsRecording(false);
      const blob = new Blob(chunksRef.current, { type: "audio/webm" });
      chunksRef.current = [];

      if (blob.size === 0) {
        setIsProcessing(false);
        toast.info("Nenhum áudio gravado. Tente novamente.");
        return;
      }

      try {
        const reader = new FileReader();
        const base64 = await new Promise<string>((resolve, reject) => {
          reader.onloadend = () => {
            const result = reader.result as string;
            const base64Data = result?.split(",")[1] ?? "";
            resolve(base64Data);
          };
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });

        const { data, error } = await supabase.functions.invoke("transcribe-whisper", {
          body: { audio: base64 },
        });

        if (error) throw error;
        const res = data as { text?: string; error?: string } | null;
        if (res?.error) throw new Error(res.error);

        const text = res?.text?.trim();
        if (text) {
          onTranscript(text);
        } else {
          toast.info("Não foi possível transcrever o áudio. Tente novamente.");
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        const errData = (err as { data?: { error?: string } })?.data?.error ?? "";
        const fullMsg = msg + errData;
        if (fullMsg.includes("413") || fullMsg.includes("muito longo")) {
          toast.error("Áudio muito longo ou pesado. Grave no máximo 5 minutos.");
        } else if (fullMsg.includes("408") || fullMsg.includes("timeout") || fullMsg.includes("Timeout")) {
          toast.error("Áudio muito longo ou pesado. Grave no máximo 5 minutos.");
        } else if (fullMsg.includes("429")) {
          toast.error("Serviço temporariamente sobrecarregado. Tente em instantes.");
        } else {
          toast.error("Erro ao transcrever. Tente novamente.");
        }
      } finally {
        setIsProcessing(false);
      }
    };
    recorder.stop();
  }, [onTranscript, stopTimer]);

  stopRecordingRef.current = stopRecording;

  // Cleanup: garante que o timer seja limpo se o componente desmontar durante gravação
  useEffect(() => () => stopTimer(), [stopTimer]);

  const toggleRecording = useCallback(() => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  }, [isRecording, startRecording, stopRecording]);

  return (
    <Button
      type="button"
      variant={isRecording ? "destructive" : "outline"}
      size="icon"
      className={`shrink-0 ${isRecording ? "animate-pulse" : ""} ${className || ""}`}
      onClick={toggleRecording}
      disabled={disabled || isProcessing}
      title={
        isRecording
          ? `Parar gravação (${secondsLeft}s restantes)`
          : "Gravar áudio (máx. 5 min)"
      }
    >
      {isProcessing ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : isRecording ? (
        <MicOff className="h-4 w-4" />
      ) : (
        <Mic className="h-4 w-4" />
      )}
    </Button>
  );
};

export default VoiceCommandButton;
